import { isFlowChild } from "./layout.ts";
import { leafRendererFor } from "./leaf.ts";
import { copyGrids, markerShows, textRows } from "./plain-text.ts";
import type { CopyGrids } from "./plain-text.ts";
import { regionOf, surfaceText } from "./native.ts";
import { renderedLines } from "./rendered-text.ts";
import type { HostPart } from "./rendered-text.ts";
import { domLength, inlineBoxesOf } from "./types.ts";
import type { CharSourceRun, LayoutNode } from "./types.ts";
import { hardLineSpans, INLINE_PAD, OBJECT_REPLACEMENT, WBR_MARKER } from "./wrap.ts";

/**
 * Character ↔ DOM position mapping over a leaf's `charSource` runs
 * (specs/semantic-selection.md): word boundaries map forward to
 * `setBaseAndExtent` points, and a Range's boundary points map
 * backward to slices of the leaf's layout text.
 */

/** Negative when point `a` precedes point `b` in DOM order, 0 when equal. */
export function comparePoints(aNode: Node, aOffset: number, bNode: Node, bOffset: number): number {
  const document = aNode.ownerDocument!;
  const a = document.createRange();
  a.setStart(aNode, aOffset);
  a.collapse(true);
  const b = document.createRange();
  b.setStart(bNode, bOffset);
  b.collapse(true);
  return a.compareBoundaryPoints(a.START_TO_START, b);
}

/** A custom leaf's transcript: the node its `selectionTarget` names
 * when that node holds the leaf's text verbatim (specs/leaf-renderers.md)
 * — a text-mode drag, the painted highlight, and the copy then read
 * positions in it as indices into `leaf.text`. */
function transcriptOf(leaf: LayoutNode): Node | null {
  const target = leafRendererFor(leaf.source.tagName)?.selectionTarget?.(leaf.source) ?? null;
  return target && target.textContent === leaf.text ? target : null;
}

/** The text node and offset `offset` code units into `container`'s
 * text; past the end, the last node's end. */
export function textPositionAt(container: Node, offset: number): [Text, number] | null {
  let remaining = offset;
  let last: [Text, number] | null = null;
  for (const text of textNodesOf(container)) {
    if (remaining <= text.data.length) return [text, remaining];
    remaining -= text.data.length;
    last = [text, text.data.length];
  }
  return last;
}

/** A boundary point inside `container` as code units into its text —
 * a Range does the flattening: its string is exactly the text between
 * the container's start and the point. Null outside it. */
export function textOffsetOf(container: Node, node: Node, offset: number): number | null {
  if (!container.contains(node)) return null;
  const range = container.ownerDocument!.createRange();
  range.selectNodeContents(container);
  range.setEnd(node, offset);
  return range.toString().length;
}

function* textNodesOf(container: Node): Generator<Text> {
  const walker = container.ownerDocument!.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    yield node as Text;
    node = walker.nextNode();
  }
}

/** The index into `leaf.text` of the character at or after a DOM
 * boundary point: a point inside a collapsed whitespace run is that
 * run's space, a point past a node's mapped characters is the next
 * mapped index (or `text.length`), a point inside an atomic inline
 * box's subtree is the box's U+FFFC marker, and a point in a custom
 * leaf's transcript its offset there. */
export function charIndexAt(leaf: LayoutNode, container: Node, offset: number): number {
  if (!leaf.charSource) {
    const transcript = transcriptOf(leaf);
    return (transcript && textOffsetOf(transcript, container, offset)) ?? 0;
  }
  // An element's points lie in its own atomic box; a pseudo-element's
  // box stands at its element's edge.
  const boxes = inlineBoxesOf(leaf);
  const markerOf = (boxIndex: number): number => {
    let marker = -1;
    for (let i = 0; i <= boxIndex; i++) marker = leaf.text.indexOf(OBJECT_REPLACEMENT, marker + 1);
    return marker;
  };
  const inside = boxes.findIndex((box) => !box.generated && box.source.contains(container));
  const marker = inside >= 0 ? markerOf(inside) : -1;
  if (marker >= 0) return marker;
  const index = runIndexAt(leaf.charSource, container, offset);
  // A point just past an atomic box, or past an inline holding one, is
  // past its marker, which no run stands for.
  const before = offset > 0 ? container.childNodes[offset - 1] : undefined;
  let past = -1;
  boxes.forEach((box, i) => {
    if (!box.generated && before?.contains(box.source)) past = i;
  });
  return past >= 0 ? Math.max(index, markerOf(past) + 1) : index;
}

/** The character a point stands before, by the runs of text it lies
 * in or past. */
function runIndexAt(runs: CharSourceRun[], container: Node, offset: number): number {
  // Runs are in DOM order: the last one starting at or before the point.
  let low = 0;
  let high = runs.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    const run = runs[mid]!;
    if (comparePoints(run.node, run.offset, container, offset) <= 0) low = mid + 1;
    else high = mid;
  }
  if (low === 0) return 0;
  // The characters standing for one cluster (a tab's spaces, `ß` as
  // `SS`) share its offset, a run each: the point is before the first.
  let found = low - 1;
  while (found > 0 && holds(runs[found - 1]!, container, offset)) found--;
  const run = runs[found]!;
  // A `::before` stands just after its point, an `::after` just before.
  if (!holds(run, container, offset) || run.pseudo === "::after") return run.index + run.length;
  return run.index + (offset - run.offset);
}

/** Whether a run holds a point: a text run each offset of its
 * characters, a pseudo-element's its one point. */
const holds = (run: CharSourceRun, container: Node, offset: number): boolean =>
  run.node === container &&
  run.offset <= offset &&
  offset < run.offset + Math.max(1, domLength(run));

/** The DOM position of `leaf.text[index]` (or of the end of the run
 * ending there) — in a custom leaf's transcript when it has one; null
 * for a character with no source position. */
export function positionOf(leaf: LayoutNode, index: number): { node: Node; offset: number } | null {
  if (!leaf.charSource) {
    const transcript = transcriptOf(leaf);
    const at = transcript && textPositionAt(transcript, index);
    return at && { node: at[0], offset: at[1] };
  }
  const runs = leaf.charSource ?? [];
  let low = 0;
  let high = runs.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (runs[mid]!.index <= index) low = mid + 1;
    else high = mid;
  }
  const run = runs[low - 1];
  if (!run) return null;
  const delta = index - run.index;
  return delta <= run.length
    ? { node: run.node, offset: run.offset + Math.min(delta, domLength(run)) }
    : null;
}

/* === Selection location ============================================= */

export interface BoundaryPoints {
  startContainer: Node;
  startOffset: number;
  endContainer: Node;
  endOffset: number;
}

/** A Range over the points, in `document`; points in two roots leave
 * it collapsed at the end. */
export function rangeOf(document: Document, points: BoundaryPoints): Range {
  const range = document.createRange();
  range.setStart(points.startContainer, points.startOffset);
  range.setEnd(points.endContainer, points.endOffset);
  return range;
}

/** The document Selection's first range as seen through `shadowRoot`
 * and the `leafRoots` (`getComposedRanges` on Firefox/WebKit and
 * standards-path Chromium; `ShadowRoot.getSelection()` as the legacy
 * Chromium fallback). A selection inside a shadow
 * root not listed comes back retargeted onto that root's host, which
 * is exactly the light-tree range around it; listing a custom leaf's
 * transcript root keeps the points inside it. Any API surprise reads
 * as no selection, never an error. */
export function selectionRangeThrough(
  shadowRoot: ShadowRoot,
  leafRoots: ShadowRoot[] = [],
): BoundaryPoints | null {
  try {
    const selection = shadowRoot.ownerDocument.getSelection();
    if (!selection) return null;
    if (selection.getComposedRanges) {
      const ranges = selection.getComposedRanges({ shadowRoots: [shadowRoot, ...leafRoots] });
      return ranges[0] ?? null;
    }
    const shadowSelection = (
      shadowRoot as { getSelection?: () => Selection | null }
    ).getSelection?.();
    if (!shadowSelection || shadowSelection.rangeCount === 0) return null;
    return shadowSelection.getRangeAt(0);
  } catch {
    return null;
  }
}

/** Where a selection lives relative to a host: in its shadow `grid`,
 * in one of its native regions (the browser's own, specs/native-regions.md
 * "Interaction"), in its light DOM (both points, the host's own child
 * list included), or anywhere else — including straddling the two. */
type SelectionKind = "grid" | "native" | "light" | "outside";

export function classifySelection(
  host: Element,
  grid: Element,
  range: BoundaryPoints,
): SelectionKind {
  if (grid.contains(range.startContainer) && grid.contains(range.endContainer)) return "grid";
  const start = lightNode(host, range.startContainer);
  const end = lightNode(host, range.endContainer);
  if (!start || !end) return "outside";
  const region = regionOf(start, host);
  return region && regionOf(end, host) === region ? "native" : "light";
}

/** The part of a selection in the host's light DOM: its points within
 * the host, the host's edges where it runs past them; null where it
 * misses the host or straddles its grid. */
export function hostPart(host: Element, points: BoundaryPoints): BoundaryPoints | null {
  // A point in the grid collapses the range, its root another.
  const range = rangeOf(host.ownerDocument, points);
  if (range.collapsed || !range.intersectsNode(host)) return null;
  const start = lightNode(host, range.startContainer) !== null;
  const end = lightNode(host, range.endContainer) !== null;
  const part = {
    startContainer: start ? range.startContainer : host,
    startOffset: start ? range.startOffset : 0,
    endContainer: end ? range.endContainer : host,
    endOffset: end ? range.endOffset : host.childNodes.length,
  };
  const touches = part.startContainer === part.endContainer && part.startOffset === part.endOffset;
  return touches ? null : part;
}

/** The node of the host's light DOM holding a node, through the shadow
 * boundaries of the host's descendants: a custom leaf's or a nested
 * host's for a node in its shadow; null for one outside the host, or in
 * its own shadow (the grid). */
function lightNode(host: Element, node: Node): Node | null {
  for (let current: Node | null = node; current;) {
    if (host.contains(current)) return current;
    const root = current.getRootNode();
    current = root instanceof ShadowRoot && root.host !== host ? root.host : null;
  }
  return null;
}

/** The shadow roots of the custom leaves with transcripts under
 * `root`, for `selectionRangeThrough`. */
export function leafShadowRoots(root: LayoutNode): ShadowRoot[] {
  const roots: ShadowRoot[] = [];
  const visit = (node: LayoutNode): void => {
    const shadow = transcriptOf(node)?.getRootNode();
    if (shadow instanceof ShadowRoot) roots.push(shadow);
    for (const child of node.children) visit(child);
  };
  visit(root);
  return roots;
}

/** A range whose points lie inside a custom leaf's transcript: the
 * leaf and the character range, or null for a light-tree range. */
function transcriptRange(
  root: LayoutNode,
  points: BoundaryPoints,
): { leaf: LayoutNode; start: number; end: number } | null {
  const shadow = points.startContainer.getRootNode();
  if (!(shadow instanceof ShadowRoot) || shadow.host === root.source) return null;
  let found: LayoutNode | null = null;
  const visit = (node: LayoutNode): void => {
    if (found) return;
    if (node.source === shadow.host) found = node;
    else for (const child of node.children) visit(child);
  };
  visit(root);
  if (!found) return null;
  const leaf: LayoutNode = found;
  const start = charIndexAt(leaf, points.startContainer, points.startOffset);
  const end = charIndexAt(leaf, points.endContainer, points.endOffset);
  return { leaf, start: Math.min(start, end), end: Math.max(start, end) };
}

/* === Painted selection =============================================== */

/** The text leaves a light-DOM range reaches, each with the character
 * range the paint inverts (specs/wide-characters.md "The grid paints
 * the selection"): a leaf inside the range whole, a boundary leaf from
 * or to the point's character, a renderer leaf whole. */
export function selectedRanges(
  root: LayoutNode,
  points: BoundaryPoints,
): Map<LayoutNode, { start: number; end: number }> {
  const inTranscript = transcriptRange(root, points);
  if (inTranscript) {
    const { leaf, start, end } = inTranscript;
    return end > start ? new Map([[leaf, { start, end }]]) : new Map();
  }
  const range = rangeOf(root.source.ownerDocument!, points);
  const ranges = new Map<LayoutNode, { start: number; end: number }>();
  const visit = (node: LayoutNode): void => {
    if (node.tableHidden || node.forceHidden || !rangeMeets(node, range)) return;
    if (isTextLeaf(node)) {
      const chars = coveredChars(node, range);
      if (chars.end > chars.start) ranges.set(node, chars);
    } else if (node.image) {
      // An atomic box, selected whole (specs/images.md "Paint").
      ranges.set(node, { start: 0, end: 1 });
    }
    for (const child of node.children) visit(child);
  };
  visit(root);
  return ranges;
}

/** The characters of a leaf's layout text a range covers: from or to a
 * boundary point inside it, all of them for a renderer leaf (its text
 * has no source positions). */
function coveredChars(leaf: LayoutNode, range: Range): { start: number; end: number } {
  let start = 0;
  let end = leaf.text.length;
  if (leaf.charSource) {
    if (leafHolds(leaf, range.startContainer)) {
      start = charIndexAt(leaf, range.startContainer, range.startOffset);
    }
    if (leafHolds(leaf, range.endContainer)) {
      end = charIndexAt(leaf, range.endContainer, range.endOffset);
    }
  }
  return { start, end };
}

/* === Copy serialization ============================================== */

/** Whether a leaf's character paints (specs/visibility.md): its inline
 * element's `visibility`, else the leaf's own. */
export function charVisible(leaf: LayoutNode, index: number): boolean {
  const inline = leaf.charInline?.[index] ?? -1;
  return inline >= 0 ? leaf.inlineElements![inline]!.visible : leaf.style.visible;
}

/** A required line break count (collapses with neighbors, dropped at
 * the ends) or literal text (a leaf's slice, a table's tab or row
 * newline) — the HTML `innerText` rendered-text items. */
type TextItem = { text: string } | { breaks: number };

/** `text/plain` for a light-DOM selection (specs/semantic-selection.md
 * "Copy serialization"): every node the range intersects, in tree
 * order, laid out by the `innerText` rules — a `<p>` surrounded by a
 * blank line, any other block-level box by one line break, table cells
 * separated by tabs and rows by newlines — over each leaf's layout
 * text. The browsers' own serializers lose block breaks for the
 * engine's out-of-flow boxes; this restores what they would have
 * produced in flow. */
export function serializeSelection(
  root: LayoutNode,
  points: BoundaryPoints,
  options: { cells?: boolean; nested?: HostPart } = {},
): string {
  const inTranscript = transcriptRange(root, points);
  if (inTranscript) return inTranscript.leaf.text.slice(inTranscript.start, inTranscript.end);
  const range = rangeOf(root.source.ownerDocument!, points);
  const items: TextItem[] = [];
  let grids: CopyGrids | undefined;
  const cells = options.cells ? () => (grids ??= copyGrids(root)) : null;
  const { nested = () => null } = options;
  collectItems(root, { range, markers: new Map(), grids: cells, nested }, items);
  return assemble(items);
}

/** The page's text a range holds before and after a host, plain
 * (`renderedLines`), the host's own left out, the host copying it, and
 * another host's part as `nested` gives it. */
export function pageAround(
  range: Range,
  host: Element,
  nested: HostPart = () => null,
): [string, string] {
  const before = range.cloneRange();
  before.setEndBefore(host);
  const after = range.cloneRange();
  after.setStartAfter(host);
  const text = (side: Range): string =>
    renderedLines(side, side.commonAncestorContainer, nested).join("\n");
  return [text(before), text(after)];
}

/** Each leaf's copied markers, which go before its text. */
type CopiedMarkers = Map<LayoutNode, string>;

/** A copy's walk: its range, the markers copied before each leaf's text,
 * and, where images copy as their cells (grid mode), its grids, rendered
 * once where an image is copied; null where images copy their alt. */
interface Copy {
  range: Range;
  markers: CopiedMarkers;
  grids: (() => CopyGrids) | null;
  nested: HostPart;
}

function collectItems(node: LayoutNode, copy: Copy, items: TextItem[]): void {
  const { range, markers } = copy;
  if (node.tableHidden || node.forceHidden || !rangeMeets(node, range)) return;
  if (node.marker && (node.text || !node.anonymous)) copyMarker(node, node, range, markers);
  // A hidden box gives up its own breaks, its subtree's items kept, as
  // innerText does (specs/visibility.md).
  const breaks = node.style.visible ? requiredBreaks(node) : 0;
  if (breaks) items.push({ breaks });
  if (node.style.tableRole === "row") {
    collectRow(node, copy, items);
  } else if (node.style.display === "table") {
    const rows = tableRows(node);
    for (const child of node.children) {
      if (child.style.tableRole === "row" || isRowGroup(child)) continue;
      collectItems(child, copy, items); // captions
    }
    // A visible row's newline, before the next row the range reaches,
    // like the browsers' own partial-table copies.
    let separated = false;
    for (const row of rows) {
      if (!range.intersectsNode(row.source)) continue;
      if (separated) items.push({ text: "\n" });
      collectItems(row, copy, items);
      separated = row.style.visible;
    }
  } else {
    // A hidden flowed region's visible contents still copy (`regionText`).
    if (node.style.visible ? node.native || node.image : node.native && !node.native.replaced) {
      items.push({ text: surfaceCopy(node, copy) });
    }
    if (isTextLeaf(node)) {
      const marker = markers.get(node);
      if (marker) items.push({ text: marker });
      items.push({ text: leafSlice(node, copy) });
    }
    for (const child of node.children) {
      if (child.inlineBox) continue;
      // An inside marker's line of its own, which holds no text.
      if (child.marker && !child.text && child.anonymous) copyMarker(child, node, range, markers);
      else collectItems(child, copy, items);
    }
  }
  if (breaks) items.push({ breaks });
}

/** A marker the grid shows, copied before the first text of `item`
 * where the range holds that text's first line whole (specs/lists.md
 * "Selection, copy and accessibility"). */
function copyMarker(
  holder: LayoutNode,
  item: LayoutNode,
  range: Range,
  markers: CopiedMarkers,
): void {
  if (!holder.style.visible || !markerShows(holder)) return;
  const leaf = firstTextLeaf(item);
  if (!leaf || !rangeMeets(leaf, range)) return;
  const { start, end } = coveredChars(leaf, range);
  const lineEnd = leaf.lines?.spans[0]?.end ?? hardLineSpans(leaf.text)[0]!.end;
  if (start > 0 || end < lineEnd) return;
  markers.set(leaf, (markers.get(leaf) ?? "") + holder.marker!.text);
}

/** The text leaf holding a box's first line: itself, or the first its
 * in-flow boxes hold. */
function firstTextLeaf(node: LayoutNode): LayoutNode | undefined {
  if (isTextLeaf(node)) return node;
  for (const child of node.children) {
    const leaf = isFlowChild(child) ? firstTextLeaf(child) : undefined;
    if (leaf) return leaf;
  }
  return undefined;
}

/** A row's cells, a visible one's tab before the next, as innerText
 * puts it after every visible cell but the last. */
function collectRow(row: LayoutNode, copy: Copy, items: TextItem[]): void {
  let separated = false;
  for (const cell of row.children) {
    if (cell.style.tableRole !== "cell" || cell.tableHidden) continue;
    if (!copy.range.intersectsNode(cell.source)) continue;
    if (separated) items.push({ text: "\t" });
    collectItems(cell, copy, items);
    separated = cell.style.visible;
  }
}

function isRowGroup(node: LayoutNode): boolean {
  const role = node.style.tableRole;
  return role === "header-group" || role === "row-group" || role === "footer-group";
}

function tableRows(table: LayoutNode): LayoutNode[] {
  const rows: LayoutNode[] = [];
  for (const child of table.children) {
    if (child.style.tableRole === "row") rows.push(child);
    else if (isRowGroup(child)) {
      for (const row of child.children) if (row.style.tableRole === "row") rows.push(row);
    }
  }
  return rows;
}

/** `innerText`: a `<p>` gets two required breaks, any other block-level
 * box (a caption included) one; inline boxes and table internals none. */
function requiredBreaks(node: LayoutNode): number {
  // An anonymous run is no element: innerText gives it no breaks of its
  // own, the blocks beside it theirs — none where they are hidden.
  if (node.inlineBox || node.anonymous) return 0;
  const role = node.style.tableRole;
  if (role === "row" || role === "cell" || isRowGroup(node)) return 0;
  if (role === "column" || role === "column-group") return 0;
  return node.source.tagName === "P" && !node.generated ? 2 : 1;
}

export interface Point {
  node: Node;
  offset: number;
}

/** A DOM range's two points. */
export interface PointRange {
  start: Point;
  end: Point;
}

/** The points around a node in its parent, as `Range.selectNode` sets
 * them; null for a node with none. */
export function pointsAround(node: Node): PointRange | null {
  const parent = node.parentNode;
  if (!parent) return null;
  const offset = Array.prototype.indexOf.call(parent.childNodes, node);
  return { start: { node: parent, offset }, end: { node: parent, offset: offset + 1 } };
}

/** The DOM extent of a leaf's run — its first mapped character or
 * inline box through its last; null for a run with neither. */
export function leafExtent(leaf: LayoutNode): PointRange | null {
  const points: PointRange[] = [];
  const runs = leaf.charSource ?? [];
  if (runs.length > 0) {
    const first = runs[0]!;
    const last = runs[runs.length - 1]!;
    points.push({
      start: { node: first.node, offset: first.offset },
      end: { node: last.node, offset: last.offset + domLength(last) },
    });
  }
  for (const box of inlineBoxesOf(leaf)) {
    // A pseudo-element's box stands at its element's edge.
    const { generated, source } = box;
    if (generated) {
      const { offset } = generated;
      points.push({ start: { node: source, offset }, end: { node: source, offset } });
      continue;
    }
    const around = pointsAround(source);
    if (around) points.push(around);
  }
  if (points.length === 0) return null;
  const before = (a: Point, b: Point) => comparePoints(a.node, a.offset, b.node, b.offset) < 0;
  let { start, end } = points[0]!;
  for (const point of points.slice(1)) {
    if (before(point.start, start)) start = point.start;
    if (before(end, point.end)) end = point.end;
  }
  return { start, end };
}

/** The light DOM an anonymous run owns — its text nodes, inline
 * elements, and children (atomic boxes, out-of-flow elements) — since
 * its `source` is the container it shares with its siblings. */
function runNodes(leaf: LayoutNode): Node[] {
  return [
    ...(leaf.charSource ?? []).map((run) => run.node),
    ...(leaf.inlineElements ?? []).map((inline) => inline.element),
    ...leaf.children.map((child) => child.source),
  ];
}

/** Whether the range reaches the node's light DOM. */
function rangeMeets(node: LayoutNode, range: Range): boolean {
  if (!node.anonymous) return range.intersectsNode(node.source);
  return runNodes(node).some((own) => range.intersectsNode(own));
}

/** Whether a range endpoint's node is the leaf's — the container
 * itself included, since a point between a run's nodes sits on it
 * (`charIndexAt` clamps one beyond the run to its ends). */
function leafHolds(leaf: LayoutNode, node: Node): boolean {
  if (!leaf.anonymous) return leaf.source.contains(node);
  return leaf.source === node || runNodes(leaf).some((own) => own === node || own.contains(node));
}

/** A node whose text is painted: text and no in-flow children (the
 * paint walk's own test); renderer leaves included. */
export function isTextLeaf(node: LayoutNode): boolean {
  if (node.text.length === 0) return false;
  return !node.children.some(
    (child) =>
      !child.inlineBox && child.style.position !== "absolute" && child.style.position !== "fixed",
  );
}

/** The part of a leaf's layout text the range covers (coveredChars),
 * with inline boxes spliced in for their U+FFFC markers and padding
 * markers dropped. A final newline (a trailing `<br>`, which the wrap
 * layer drops) goes too. */
function leafSlice(leaf: LayoutNode, copy: Copy): string {
  const { range } = copy;
  const { text } = leaf;
  const { start, end } = coveredChars(leaf, range);
  // Hidden text is no rendered text (specs/visibility.md).
  const last = end === text.length && text.endsWith("\n") ? end - 1 : end;
  let slice = "";
  for (let k = start; k < last; k++) {
    const char = text[k]!;
    if (char === OBJECT_REPLACEMENT || charVisible(leaf, k)) slice += char;
  }
  const boxes = inlineBoxesOf(leaf);
  let boxIndex = text.slice(0, start).split(OBJECT_REPLACEMENT).length - 1;
  slice = slice.replaceAll(OBJECT_REPLACEMENT, () => {
    const box = boxes[boxIndex++];
    if (!box || !range.intersectsNode(box.source)) return "";
    const items: TextItem[] = [];
    collectItems(box, { ...copy, markers: new Map() }, items);
    return assemble(items);
  });
  return slice.replaceAll(INLINE_PAD, "").replaceAll(WBR_MARKER, "");
}

/** An image's or a region's copy (specs/images.md "The light DOM",
 * specs/native-regions.md "Interaction"): its alt or its text, as
 * rendered; in grid mode its cells as a copy's grids hold them. */
function surfaceCopy(node: LayoutNode, copy: Copy): string {
  if (copy.grids) return textRows(copy.grids(), node).join("\n");
  return surfaceText(node, copy.range, copy.nested);
}

/** Required breaks collapse to the largest of a run and vanish at
 * either end; text items concatenate. */
function assemble(items: TextItem[]): string {
  let out = "";
  let pending = 0;
  for (const item of items) {
    if ("breaks" in item) {
      pending = Math.max(pending, item.breaks);
      continue;
    }
    if (item.text.length === 0) continue;
    if (out.length > 0 && pending > 0) out += "\n".repeat(pending);
    pending = 0;
    out += item.text;
  }
  return out;
}

/* === Words ============================================================ */

const segmenters = new Map<string, Intl.Segmenter | null>();

/** The word containing `leaf.text[index]` — the `Intl.Segmenter`
 * segment (word-like or not) within the run of text between markers
 * and newlines, in the element's language. null off a character, at a
 * marker, or without a Segmenter. */
export function wordAt(leaf: LayoutNode, index: number): { start: number; end: number } | null {
  const { text } = leaf;
  if (index < 0 || index >= text.length || isWordBoundary(text[index]!)) return null;
  let start = index;
  while (start > 0 && !isWordBoundary(text[start - 1]!)) start--;
  let end = index;
  while (end < text.length && !isWordBoundary(text[end]!)) end++;
  const segmenter = segmenterFor(leaf.source.closest?.("[lang]")?.getAttribute("lang") ?? "");
  if (!segmenter) return null;
  for (const segment of segmenter.segment(text.slice(start, end))) {
    const from = start + segment.index;
    const to = from + segment.segment.length;
    if (index >= from && index < to) return { start: from, end: to };
  }
  return null;
}

function isWordBoundary(ch: string): boolean {
  return ch === "\n" || ch === OBJECT_REPLACEMENT || ch === INLINE_PAD;
}

/** One Segmenter per language, cached (construction is not free and
 * word extension asks per pointermove); an invalid tag falls back to
 * the default locale. */
function segmenterFor(lang: string): Intl.Segmenter | null {
  let segmenter = segmenters.get(lang);
  if (segmenter === undefined) {
    segmenter = createSegmenter(lang) ?? createSegmenter("");
    segmenters.set(lang, segmenter);
  }
  return segmenter;
}

function createSegmenter(lang: string): Intl.Segmenter | null {
  if (typeof Intl.Segmenter !== "function") return null;
  try {
    return new Intl.Segmenter(lang || undefined, { granularity: "word" });
  } catch {
    return null;
  }
}
